import { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { parseBackgroundMedia } from "../components/backgroundMedia.jsx";
import ContentText from "../components/contentText.jsx";
import PageHero from "../components/pageHero.jsx";
import SectionContent from "../components/sectionContent.jsx";
import { SiteContentContext } from "../context/SiteContentContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { absoluteUrl } from "../seoUtils";

/* ─── Stat icon SVGs ─── */
function FactoryIcon() {
  return (
    <svg
      className="w-6 h-6 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <title>Fabrik</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
      />
    </svg>
  );
}
function AreaIcon() {
  return (
    <svg
      className="w-6 h-6 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <title>Yta</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
      />
    </svg>
  );
}
function PatentIcon() {
  return (
    <svg
      className="w-6 h-6 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <title>Patent</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
      />
    </svg>
  );
}
function PartnersIcon() {
  return (
    <svg
      className="w-6 h-6 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <title>Partners</title>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
      />
    </svg>
  );
}

const STAT_ICONS = [
  <FactoryIcon key="factory" />,
  <AreaIcon key="area" />,
  <PatentIcon key="patent" />,
  <PartnersIcon key="partners" />,
];

/* ─── Featured manufacturer ─── */
function FeaturedManufacturerSection() {
  const { getContent, contentLoaded } = useContext(SiteContentContext);

  const logoUrl = getContent("about.featured_logo", "");
  const description = getContent("about.featured_description", "");
  const media = parseBackgroundMedia(getContent("about.featured_media"));

  const stats = [1, 2, 3, 4].map((n) => ({
    value: getContent(`about.stat_${n}_value`, ""),
    label: getContent(`about.stat_${n}_label`, ""),
    icon: STAT_ICONS[n - 1],
  }));

  const hasContent = logoUrl || description || stats.some((s) => s.value);
  if (!hasContent) return null;

  return (
    <section className="bg-white py-16 md:py-24">
      <SectionContent>
        {/* Logo */}
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Tillverkare logotyp"
            className="h-24 md:h-32 mb-8 object-contain"
          />
        )}

        {/* Two-column: text+stats | media */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: description + stats */}
          <div>
            {description && (
              <p className="text-gray-700 text-base leading-relaxed mb-10">
                <ContentText contentKey="about.featured_description" fallback="" />
              </p>
            )}
            {stats.some((s) => s.value) && (
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                {stats
                  .filter((s) => s.value)
                  .map((stat) => (
                    <div key={stat.label} className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0">{stat.icon}</div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 leading-tight">
                          {stat.value}
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5">{stat.label}</p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Right: image / video */}
          {contentLoaded && (media.image || media.video) && (
            <div className="relative rounded-xl overflow-hidden aspect-video bg-gray-100">
              {media.video ? (
                /* biome-ignore lint/a11y/useMediaCaption: manufacturer promo video */
                <video
                  src={media.video}
                  poster={media.image || undefined}
                  controls
                  className="w-full h-full object-cover"
                  preload="metadata"
                />
              ) : (
                <img src={media.image} alt="Tillverkare" className="w-full h-full object-cover" />
              )}
            </div>
          )}
        </div>
      </SectionContent>
    </section>
  );
}

/* ─── Secondary manufacturers ─── */
function SecondaryManufacturersSection() {
  const { getContent } = useContext(SiteContentContext);

  const manufacturers = [1, 2, 3, 4]
    .map((n) => ({
      logo: getContent(`about.manufacturer_${n}_logo`, ""),
      description: getContent(`about.manufacturer_${n}_description`, ""),
      key: n,
    }))
    .filter((m) => m.logo || m.description);

  if (manufacturers.length === 0) return null;

  return (
    <section className="bg-white border-t border-gray-100 py-16 md:py-24">
      <SectionContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          {manufacturers.map((m) => (
            <div key={m.key}>
              {m.logo && (
                <img src={m.logo} alt="Tillverkare logotyp" className="h-20 mb-6 object-contain" />
              )}
              {m.description && (
                <p className="text-gray-700 text-base leading-relaxed">
                  <ContentText contentKey={`about.manufacturer_${m.key}_description`} fallback="" />
                </p>
              )}
            </div>
          ))}
        </div>
      </SectionContent>
    </section>
  );
}

/* ─── Page ─── */
const AboutUs = () => {
  const { locale } = useLocalePath();
  const aboutUrl = absoluteUrl(locale, "/about-us");

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>AS Maskiner - Om oss | Ledande leverantör av nya och begagnade maskiner</title>
        <meta
          name="description"
          content="Lär känna AS Maskiner, ledande leverantör av nya och begagnade kvalitetsmaskiner för trä- och plastindustrin."
        />
        <meta property="og:url" content={aboutUrl} />
        <meta
          property="og:title"
          content="Om AS Maskiner - Ledande leverantör av nya och begagnade maskiner"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            url: aboutUrl,
            name: "Om oss",
          })}
        </script>
      </Helmet>

      <PageHero
        titleKey="about.hero_title"
        titleFallback="Leverantörer i världsklass"
        subtitleKey="about.hero_body"
        backgroundKey="about.hero_background"
        alignment="bottom"
      />
      <FeaturedManufacturerSection />
      <SecondaryManufacturersSection />
    </div>
  );
};

export default AboutUs;
